<script setup lang="ts">
import type { UpcomingPresentation } from '~/api/queries/developerPresentations';
import { downloadPresentationIcs, useUpcomingPresentationsQuery } from '~/api/queries/developerPresentations';

const props = defineProps<{ developerId: number; projectId?: number }>();

const { t, locale } = useI18n();

const { data } = useUpcomingPresentationsQuery(
  () => props.developerId,
  () => props.projectId,
);

const items = computed(() => data.value?.data ?? []);
const signedIn = computed(() => !!data.value?.signedIn);

// Viewer's locale and own time zone.
const dateFormatter = computed(
  () => new Intl.DateTimeFormat(locale.value, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
);
const timeFormatter = computed(
  () => new Intl.DateTimeFormat(locale.value, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }),
);

const formatDate = (iso: string) => dateFormatter.value.format(new Date(iso));
const formatTime = (iso: string) => timeFormatter.value.format(new Date(iso));

const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return t('devRepPresentations.upcoming.durationHoursMinutes', { hours: h, minutes: m });
  if (h) return t('devRepPresentations.upcoming.durationHours', { hours: h });
  return t('devRepPresentations.upcoming.durationMinutes', { minutes: m });
};

const downloading = ref<string | null>(null);
const calendarError = ref<string | null>(null);

const addToCalendar = async (p: UpcomingPresentation) => {
  downloading.value = p.id;
  calendarError.value = null;
  try {
    await downloadPresentationIcs(p);
  } catch {
    calendarError.value = p.id;
  } finally {
    downloading.value = null;
  }
};
</script>

<template>
  <section
    v-if="items.length"
    class="rounded-[18px] border border-black/[0.08] bg-white px-5 py-5 tablet:px-6"
  >
    <header class="mb-4 flex items-center justify-between gap-3">
      <h2 class="font-serif text-[18px] font-bold tracking-[-0.015em] text-black-100">
        {{ t('devRepPresentations.upcoming.title') }}
      </h2>
      <span class="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-black-60">
        {{ t('devRepPresentations.upcoming.count', { count: items.length }) }}
      </span>
    </header>

    <ul class="flex flex-col gap-3">
      <li
        v-for="p in items"
        :key="p.id"
        class="rounded-xl border border-black/[0.06] bg-[#FAF8F3] p-4"
      >
        <div class="flex flex-col gap-3 tablet:flex-row tablet:items-start">
          <div
            class="flex w-full shrink-0 flex-row items-baseline gap-2 tablet:w-[150px] tablet:flex-col tablet:gap-0.5"
          >
            <span class="text-[13px] font-semibold text-black-100">{{ formatDate(p.startsAt) }}</span>
            <span class="text-[12.5px] text-black-70">{{ formatTime(p.startsAt) }}</span>
            <span class="font-mono text-[11px] uppercase tracking-[0.06em] text-black-60">
              {{ formatDuration(p.durationMinutes) }}
            </span>
          </div>

          <div class="min-w-0 flex-1">
            <h3 class="text-[15px] font-semibold leading-snug text-black-100">{{ p.title }}</h3>
            <p
              v-if="p.projectName && !props.projectId"
              class="mt-0.5 text-[12.5px] text-black-60"
            >
              {{ t('devRepPresentations.upcoming.project', { name: p.projectName }) }}
            </p>
            <p
              v-if="p.description"
              class="mt-1.5 line-clamp-3 whitespace-pre-line text-[13px] leading-relaxed text-black-80"
            >
              {{ p.description }}
            </p>

            <ul
              v-if="p.materials.length"
              class="mt-2 flex flex-wrap gap-1.5"
            >
              <li
                v-for="m in p.materials"
                :key="m.url"
              >
                <a
                  :href="m.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 rounded-full border border-black/[0.08] bg-white px-2.5 py-1 text-[12px] text-black-80 hover:bg-black-05"
                >
                  <span class="font-mono text-[10px] font-bold text-coral-alert">{{
                    t('devRepPresentations.upcoming.pdf')
                  }}</span>
                  <span class="max-w-[200px] truncate">{{ m.name }}</span>
                </a>
              </li>
            </ul>

            <div class="mt-3 flex flex-wrap items-center gap-2">
              <a
                v-if="p.meetingLink"
                :href="p.meetingLink"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex h-9 items-center rounded-[10px] bg-black-100 px-3.5 text-[12.5px] font-semibold text-white hover:bg-black-90"
              >
                {{ t('devRepPresentations.upcoming.join') }}
              </a>
              <NuxtLink
                v-else-if="!signedIn"
                to="/login"
                class="inline-flex h-9 items-center rounded-[10px] border border-black/[0.08] bg-white px-3.5 text-[12.5px] font-semibold text-black-100 hover:bg-black-05"
              >
                {{ t('devRepPresentations.upcoming.signInForLink') }}
              </NuxtLink>
              <button
                type="button"
                :disabled="downloading === p.id"
                class="inline-flex h-9 items-center rounded-[10px] border border-black/[0.08] bg-white px-3.5 text-[12.5px] font-semibold text-black-100 hover:bg-black-05 disabled:opacity-50"
                @click="addToCalendar(p)"
              >
                {{ t('devRepPresentations.upcoming.addToCalendar') }}
              </button>
              <span
                v-if="calendarError === p.id"
                class="text-[12px] text-error-100"
              >
                {{ t('devRepPresentations.upcoming.calendarError') }}
              </span>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </section>
</template>
